import { ReactNode, FC, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import BackToTopButton from 'src/components/BackToTopButton'
import { PaymentFailedBanner } from 'src/components/PaymentFailedBanner'
import { PaymentSuccessBanner } from 'src/components/PaymentSuccessBanner'
import { TrialExpiredBanner } from 'src/components/TrialExpiredBanner'
import { useGetTrialStatusQuery } from 'src/reactQuery/useGetTrialStatusQuery'
import { useGetUserFeaturesQuery } from 'src/reactQuery/useGetUserFeaturesQuery'
import {
  deriveBannerState,
  hasBannerContent,
  readCachedBannerState,
  writeCachedBannerState,
} from './bannerState'
import styles from './layout.module.scss'
import { TopBar } from './TopBar'
import { Sidebar } from './Sidebar'
import { Footer } from './Footer'

interface LayoutProps {
  children: ReactNode
}

// Fallback only. The banner state is cached (and prefetched at login), so we
// normally know it before the first paint and nothing animates at all — the
// sidebar is simply already collapsed when the user arrives, which is the
// point. This delay covers the one case where we get it late anyway: the cache
// was cleared and the login prefetch failed. Then the page has settled, and a
// deliberate drop-in reads better than an abrupt jump.
const BANNER_REVEAL_MIN_DELAY_MS = 900

// Upper bound on the idle wait that follows the floor, so a permanently busy
// thread can never withhold the banner indefinitely.
const BANNER_REVEAL_IDLE_TIMEOUT_MS = 1200

export const Layout: FC<LayoutProps> = ({ children }) => {
  const { data: trialStatusResponse, isSuccess: isTrialResolved } =
    useGetTrialStatusQuery()
  const { data: userFeaturesResponse, isSuccess: isFeaturesResolved } =
    useGetUserFeaturesQuery()
  const [searchParams, setSearchParams] = useSearchParams()

  // Read synchronously on first render — a useEffect would run after paint and
  // reintroduce the very shift we are removing.
  const [cachedBannerState] = useState(readCachedBannerState)

  const trialData = trialStatusResponse?.data
  const featuresData = userFeaturesResponse?.data

  // Both queries have answered; until then we trust the cached state. Keyed on
  // isSuccess rather than the payload, so a null response (no session) still
  // counts as resolved and correctly clears the banner instead of trusting a
  // stale cache forever.
  const isResolved = isTrialResolved && isFeaturesResolved

  const liveBannerState = deriveBannerState(trialData, featuresData)

  const bannerState = isResolved ? liveBannerState : cachedBannerState

  // liveBannerState is rebuilt every render, so key the effect on its value
  // rather than its identity — otherwise it would write on every render.
  const serializedLiveBannerState = JSON.stringify(liveBannerState)
  useEffect(() => {
    if (!isResolved) return
    writeCachedBannerState(JSON.parse(serializedLiveBannerState))
  }, [isResolved, serializedLiveBannerState])

  const { customMessage } = bannerState
  const hasCustomMessage =
    !!customMessage?.title?.trim() || !!customMessage?.text?.trim()

  // Card-on-file payment failure — red banner. Takes priority over the
  // success/trial banners.
  const showPaymentFailedBanner = bannerState.isPaymentFailed

  // Shown once after returning from a successful Stripe Checkout
  // (/subscription/success redirects here with ?checkout=success). Dismissible;
  // takes priority over the trial banner (a user who just paid isn't expired).
  const showSuccessBanner =
    !showPaymentFailedBanner && searchParams.get('checkout') === 'success'
  const showTrialBanner =
    !showPaymentFailedBanner &&
    !showSuccessBanner &&
    (bannerState.isTrialExpired || hasCustomMessage)
  const showBanner =
    showPaymentFailedBanner || showSuccessBanner || showTrialBanner

  // This flag drives the banner, the 72px padding and the Sidebar collapse
  // together, so they always move as one motion.
  //
  // It starts from the *cached* state rather than false: when we already know a
  // banner is due, the very first paint renders it — banner present, padding
  // applied, sidebar collapsed — and the user never perceives a transition,
  // because CSS transitions only run on a change and nothing changed. Starting
  // at false and correcting was what made the shift visible; we had the answer
  // in hand and animated to it anyway.
  const [isBannerRevealed, setIsBannerRevealed] = useState(() =>
    hasBannerContent(readCachedBannerState())
  )

  useEffect(() => {
    if (!showBanner) {
      setIsBannerRevealed(false)
      return undefined
    }

    // Already up from the cached first paint — nothing to reveal.
    if (isBannerRevealed) return undefined

    const idleWindow = window as typeof window & {
      cancelIdleCallback?: (handle: number) => void
      requestIdleCallback?: (
        callback: () => void,
        options?: { timeout: number }
      ) => number
    }

    const reveal = () => setIsBannerRevealed(true)
    let idleHandle: number | undefined

    // Floor first, then idle — the page gets its time to load either way.
    const delay = window.setTimeout(() => {
      if (typeof idleWindow.requestIdleCallback === 'function') {
        idleHandle = idleWindow.requestIdleCallback(reveal, {
          timeout: BANNER_REVEAL_IDLE_TIMEOUT_MS,
        })
        return
      }
      // Safari (and older browsers) lack requestIdleCallback — the floor alone
      // is a good enough approximation there.
      reveal()
    }, BANNER_REVEAL_MIN_DELAY_MS)

    return () => {
      window.clearTimeout(delay)
      if (idleHandle !== undefined) idleWindow.cancelIdleCallback?.(idleHandle)
    }
  }, [showBanner, isBannerRevealed])

  const handleCloseSuccessBanner = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('checkout')
    setSearchParams(next, { replace: true })
  }

  return (
    <div
      className={styles.mainContainer}
      style={isBannerRevealed ? { paddingTop: '72px' } : undefined}
    >
      {isBannerRevealed && showPaymentFailedBanner && <PaymentFailedBanner />}
      {isBannerRevealed && showSuccessBanner && (
        <PaymentSuccessBanner onClose={handleCloseSuccessBanner} />
      )}
      {isBannerRevealed && showTrialBanner && (
        <TrialExpiredBanner customMessage={customMessage} />
      )}

      <Sidebar collapseGroups={isBannerRevealed} />

      <div className={styles.mainContainerInner}>
        <TopBar />

        <BackToTopButton />

        <div className={styles.contentWrapper}>{children}</div>

        <Footer />
      </div>
    </div>
  )
}
